/**
 * RuntimeSection — a prova de runtime na Conferência (Contract-Driven, Onda 4 —
 * SPEC-ONDA-4 FR-16).
 *
 * A plataforma sobe a stack do projeto (docker compose), confere o health de cada
 * componente do runtime manifest e roda o E2E na mesma subida. Aqui aparece a matriz
 * componente × etapa (build · subida · health · E2E) da última rodada, com a evidência e,
 * quando houver, o log — o log fica só na plataforma, nunca no repositório do cliente.
 *
 * `runtime` null/undefined → não renderiza nada.
 */
import { C } from '@/theme/palette';

export const ETAPAS = [['build', 'Build'], ['up', 'Subida'], ['health', 'Health'], ['e2e', 'E2E']];
export const STATUS_RUNTIME = {
  passou: ['passou', C.ok],
  falhou: ['falhou', C.bad],
  nao_conferido: ['não conferido', C.neutralMuted],
};
export const ESTAGIO = {
  passou: 'O projeto sobe e responde.',
  falhou: 'O projeto NÃO roda como stack — a entrega sai não verificada.',
  nao_conferido: 'A stack não pôde ser conferida.',
};

function Celula({ etapa }) {
  if (!etapa) return <td style={{ padding: '6px 8px', color: C.neutralMuted }}>—</td>;
  const [rotulo, cor] = STATUS_RUNTIME[etapa.status] ?? [etapa.status, C.neutralDim];
  return (
    <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: cor }}>{rotulo}</span>
      {etapa.evidencia && (
        <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 2 }}>{etapa.evidencia}</div>
      )}
      {etapa.log && (
        <details style={{ marginTop: 4 }}>
          <summary style={{ fontSize: 11, color: C.neutralDim, cursor: 'pointer' }}>Log</summary>
          <pre style={{ fontSize: 10.5, color: C.neutralMuted, whiteSpace: 'pre-wrap',
                        maxHeight: 240, overflow: 'auto', margin: '4px 0 0' }}>{etapa.log}</pre>
        </details>
      )}
    </td>
  );
}

export function RuntimeSection({ runtime }) {
  if (!runtime) return null;
  const componentes = runtime.componentes ?? [];
  const [, corEstagio] = STATUS_RUNTIME[runtime.estagio] ?? ['', C.neutralDim];
  return (
    <section aria-label="Runtime" style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 14, fontWeight: 700, color: C.neutral, margin: '0 0 4px' }}>
        Runtime
      </h2>
      <p style={{ fontSize: 12, color: corEstagio, margin: '0 0 12px' }}>
        {ESTAGIO[runtime.estagio] ?? runtime.estagio}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: C.neutralDim }}>
                Componente
              </th>
              {ETAPAS.map(([k, rotulo]) => (
                <th key={k} style={{ textAlign: 'left', padding: '6px 8px', color: C.neutralDim }}>
                  {rotulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {componentes.map((c) => (
              <tr key={`${c.repo}:${c.componente}`}
                  style={{ borderTop: `1px solid ${C.blackBorder}` }}>
                <td style={{ padding: '6px 8px', color: C.neutral, verticalAlign: 'top' }}>
                  {c.componente === 'compose' ? 'stack (docker compose)' : c.componente}
                  <div style={{ fontSize: 10.5, color: C.neutralMuted }}>
                    {c.kind}{c.repo ? ` · ${c.repo}` : ''}
                  </div>
                </td>
                {ETAPAS.map(([k]) => <Celula key={k} etapa={c.etapas?.[k]} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default RuntimeSection;
